<template>
  <v-menu
    v-if="canReadPerson"
    v-model="isOpen"
    :close-on-content-click="false"
    offset-y
    min-width="350"
    max-width="450"
  >
    <template #activator="{ on, attrs }">
      <v-btn
        v-bind="attrs"
        v-on="on"
        icon
        x-small
        class="mt-1"
        :aria-label="$t('components.template.dialog.preview.searchReceiver')"
        :title="$t('components.template.dialog.preview.searchReceiver')"
        data-test-person-lookup
      >
        <v-icon small>mdi-account-search</v-icon>
      </v-btn>
    </template>
    <v-card>
      <v-card-text class="pb-0">
        <v-autocomplete
          v-model="selectedPerson"
          :items="filteredPersons"
          :loading="isSearchingPersons"
          :search-input.sync="personSearchPattern"
          item-text="titleReadable"
          item-value="id"
          :label="$t('components.template.dialog.preview.searchReceiver')"
          :placeholder="$t('components.partner.PartnerReportCreateForm.driverPlaceholder')"
          return-object
          clearable
          outlined
          dense
          autofocus
          :no-filter="true"
          @update:search-input="debouncePersonSearch"
        >
          <template #no-data>
            <v-list-item v-if="isSearchingPersons">
              <v-list-item-content>
                <v-list-item-title>
                  {{ $t("components.partner.PartnerReportCreateForm.searchingPersons") }}
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-list-item v-else>
              <v-list-item-content>
                <v-list-item-title>
                  {{
                    personSearchPattern && personSearchPattern.length >= 2
                      ? $t("components.partner.PartnerReportCreateForm.noPersonsFound")
                      : $t("components.partner.PartnerReportCreateForm.searchForPerson")
                  }}
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </template>
          <template #item="{ item }">
            <refs-person :item="item">
              <template #icon>
                <v-list-item-avatar>
                  <v-icon>mdi-account</v-icon>
                </v-list-item-avatar>
              </template>
            </refs-person>
          </template>
          <template #selection="{ item }">
            <v-icon small color="success" class="mr-2">mdi-check-circle</v-icon>
            <span class="pr-2">{{ item.firstName }} {{ item.lastName }}</span>
          </template>
        </v-autocomplete>

        <v-select
          v-if="selectedPerson && selectedPerson.emails && selectedPerson.emails.length > 1"
          v-model="selectedPersonEmail"
          :items="selectedPerson.emails"
          item-text="email"
          item-value="email"
          :label="$t('common.nouns.email')"
          outlined
          dense
        />
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn text small @click="isOpen = false">
          {{ $t("close") }}
        </v-btn>
        <v-btn color="info" small elevation="0" :disabled="!resolvedPersonEmail" @click="apply">
          {{ $t("confirm") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-menu>
</template>

<script lang="ts">
import { Component, Vue, Prop, Watch } from "vue-property-decorator";
import { debounce } from "debounce";
import { PersonSearchModule } from "@/store/modules/search.store";
import { UserModule } from "@/store/modules/me-user.store";
import { ActionEnum } from "@/store/enum/authActionEnum";
import { ResourceEnum } from "@/store/enum/authResourceEnum";
import { IPerson } from "@/models/person.entity";
import { MrfiktivPersonControllerGetParamsGen } from "@/services/mrfiktiv/v1/data-contracts";
import RefsPerson from "@/components/utility/RefsPerson.vue";

/** What `select` emits: the picked person and the email chosen for them. */
export interface IPersonLookupSelection {
  person: IPerson;
  email: string;
}

/**
 * An account-search button that opens a menu to look up a person of the partner and pick one of their emails.
 * Meant for the `append` slot of an email field. Hidden without permission to read persons.
 */
@Component({ components: { RefsPerson } })
export default class PersonLookupMenu extends Vue {
  @Prop()
  partnerId?: string;

  isOpen = false;
  personSearchPattern = "";
  isSearchingPersons = false;
  selectedPerson: IPerson | null = null;
  selectedPersonEmail: string | null = null;

  debouncePersonSearch = debounce((val: string) => {
    this.personSearchPattern = val;
    this.searchPersons();
  }, 300);

  get filteredPersons(): IPerson[] {
    return (PersonSearchModule.filtered as IPerson[]) || [];
  }

  get canReadPerson(): boolean {
    return UserModule.abilities.can(ActionEnum.READ, ResourceEnum.PERSON, this.partnerId);
  }

  /** The email to apply: the explicit pick, or the person's first email. */
  get resolvedPersonEmail(): string | null {
    if (this.selectedPersonEmail) {
      return this.selectedPersonEmail;
    }
    return this.selectedPerson?.emails?.[0]?.email ?? null;
  }

  async searchPersons() {
    const partnerId = this.partnerId;
    if (!this.personSearchPattern || this.personSearchPattern.length < 2 || !partnerId) return;
    this.isSearchingPersons = true;
    try {
      PersonSearchModule.setSearch(this.personSearchPattern);
      const query: MrfiktivPersonControllerGetParamsGen = { partnerId };
      await PersonSearchModule.fetchPage(query);
    } catch (error) {
      this.$log.error(error);
    } finally {
      this.isSearchingPersons = false;
    }
  }

  @Watch("selectedPerson")
  onPersonSelected() {
    this.selectedPersonEmail = this.selectedPerson?.emails?.[0]?.email ?? null;
  }

  apply() {
    const email = this.resolvedPersonEmail;
    if (!email || !this.selectedPerson) return;
    const selection: IPersonLookupSelection = { person: this.selectedPerson, email };
    this.$emit("select", selection);
    this.selectedPerson = null;
    this.selectedPersonEmail = null;
    this.personSearchPattern = "";
    this.isOpen = false;
  }
}
</script>
